import React, { createContext, useEffect } from "react";
import { useRoutes, useNavigate, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { routes } from "./routes";
import { Backdrop, Box, CircularProgress } from "@mui/material";
import { ErrorPopup } from "./components/errorPopup";
import axios from "axios";
import mixpanel from "mixpanel-browser";

import { checkSession, setErrors, setShowErrors, setLoading } from "./actions/user";

import "react-toastify/dist/ReactToastify.css";
import { usePopups } from "hooks/usePopups";
import TermsOfUsePopup from "components/TermsOfUsePopup";
import { MIX_PANEL_ACTION_TYPES, MIX_PANEL_ACTIONS } from "constants/common.constants";
import { gaClickCallback } from "helpers/common";
import { HeaderProvider } from "providers/HeaderProvider";

const API_URL = process.env.REACT_APP_API_URL;
const REACT_APP_MIX_PANEL = process.env.REACT_APP_MIX_PANEL;

export const PopupContext = createContext();

function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const loading = useSelector((state) => state.userReducer.loading);
  const { data } = useSelector((state) => state.userReducer);

  const allowedPaths = ["/success-verification", "/error-verification", "/demo"];
  const urlsForErrorSkip = [
    `${API_URL}/analyses/v2/statGPT/`,
    `${API_URL}/gpt/get_dataset_validation_only_V2/`,
  ];

  useEffect(() => {
    const sessionToken = localStorage.getItem("session_token");

    if (!sessionToken && !allowedPaths.includes(window.location.pathname)) return navigate("/");

    axios.interceptors.response.use(
      function (response) {
        return response;
      },
      function (error) {
        if (error.code === "ERR_NETWORK") {
          dispatch(setLoading(false));
          dispatch(setErrors([{ msg: "Something went wrong" }]));
          dispatch(setShowErrors(true));
          return;
        }
        if (401 === error.response.status) {
          navigate("/");
        } else {
          dispatch(setLoading(false));
          if (!urlsForErrorSkip.some((url) => error.config.url.startsWith(url))) {
            dispatch(setErrors(error.response.data.detail));
            dispatch(setShowErrors(true));
          }
          return Promise.reject(error);
        }
      }
    );
    if (!allowedPaths.includes(window.location.pathname)) {
      dispatch(checkSession(() => navigate("/genie-gpt")));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, navigate]);

  useEffect(() => {
    mixpanel.init(REACT_APP_MIX_PANEL, {
      debug: true,
      track_pageview: false,
      persistence: "localStorage",
    });
  }, []);

  useEffect(() => {
    gaClickCallback({
      type: MIX_PANEL_ACTION_TYPES.PAGE,
      action: MIX_PANEL_ACTIONS.VIEW,
      label: location.pathname,
    });
  }, [location.pathname]);

  useEffect(() => {
    if (!data?.id) return;

    mixpanel.identify(data?.id);

    mixpanel.people.set({
      $name: `${data?.first_name} ${data?.last_name}`,
      $email: data?.email,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id]);

  const content = useRoutes(routes);

  const popupsState = usePopups();

  return (
    <div className="App">
      <PopupContext.Provider value={popupsState}>
        <HeaderProvider>
          {content}
          <ToastContainer position="top-right" autoClose={5000} theme={"colored"} />
          <ErrorPopup />
          <TermsOfUsePopup />
          <Backdrop sx={{ color: "#fff", zIndex: 10001 }} open={loading}>
            <Box display={"flex"} flexDirection="column" alignItems={"center"}>
              <CircularProgress color="inherit" />
            </Box>
          </Backdrop>
        </HeaderProvider>
      </PopupContext.Provider>
    </div>
  );
}

export default App;
